import { useEffect, useMemo, useState } from "react";
import client, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export const AdminClockInDialog = ({ open, onOpenChange, onDone, clockedInIds = [] }) => {
  const [sites, setSites] = useState([]);
  const [staff, setStaff] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [workerId, setWorkerId] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [time, setTime] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { return; }
    setSiteId(""); setWorkerId(""); setShowAll(false); setTime("");
    client.get("/sites").then((r) => setSites(r.data || [])).catch(() => {});
    client.get("/users").then((r) => setStaff((r.data || []).filter((u) => u.status !== "deactivated" && u.operationalAccess !== "FIELD_ONLY"))).catch(() => {});
  }, [open]);

  const site = sites.find((s) => s.id === siteId);
  const options = useMemo(() => {
    const assigned = new Set(site?.assignedCleanerIds || []);
    return staff
      .filter((u) => !clockedInIds.includes(u.id) && (showAll || assigned.has(u.id)))
      .sort((a, b) => (a.displayName || "").localeCompare(b.displayName || ""));
  }, [staff, site, showAll, clockedInIds]);

  const submit = async () => {
    setSaving(true);
    try {
      const body = { workerId, siteId };
      if (time) {
        const d = new Date();
        const [h, m] = time.split(":").map(Number);
        d.setHours(h, m, 0, 0);
        body.at = d.toISOString();
      }
      await client.post("/attendance/admin/start", body);
      const name = staff.find((u) => u.id === workerId)?.displayName;
      toast.success(`${name} clocked in at ${site?.name}`);
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="admin-clockin-dialog">
        <DialogHeader><DialogTitle>Clock someone in</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Site</Label>
            <Select value={siteId} onValueChange={(v) => { setSiteId(v); setWorkerId(""); }}>
              <SelectTrigger className="mt-1.5" data-testid="admin-clockin-site"><SelectValue placeholder="Choose a site" /></SelectTrigger>
              <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>Staff member</Label>
              <label className="flex items-center gap-2 text-xs text-slate-500">Show all staff<Switch checked={showAll} onCheckedChange={setShowAll} data-testid="admin-clockin-showall" /></label>
            </div>
            <Select value={workerId} onValueChange={setWorkerId} disabled={!siteId}>
              <SelectTrigger className="mt-1.5" data-testid="admin-clockin-worker"><SelectValue placeholder={siteId ? (options.length ? "Choose staff" : "No assigned staff — turn on Show all") : "Choose a site first"} /></SelectTrigger>
              <SelectContent>{options.map((u) => <SelectItem key={u.id} value={u.id}>{u.displayName}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Clock-in time (today)</Label>
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1.5 w-40" data-testid="admin-clockin-time" />
            <p className="text-[11px] text-slate-500 mt-1">Leave empty for now. Set an earlier time if they forgot to clock in.</p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={!siteId || !workerId || saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="admin-clockin-submit">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Clock in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
